/**
 * In-process transport: the backend core runs in the same JS context (default offline mode of the game).
 * Requests and events are passed as JSON copies and delivered asynchronously, so the game observes the
 * same semantics (no shared references, no re-entrancy) as with a network backend.
 */
import { cloneJson } from '../../shared/json';
import type { Clock } from '../../shared/clock';
import type { EventEnvelope, RequestEnvelope, ResponseEnvelope } from '../../shared/protocol/envelope';
import { BackendCore, type BackendCoreOptions } from '../../server/core/backend';
import type { DeepPartial, BackendConfig } from '../../server/core/config';
import type { ConnectionContext } from '../../server/core/hub';
import { type PasswordHasher, WebCryptoPbkdf2Hasher } from '../../server/core/password';
import { type StorageAdapter, MemoryStorage } from '../../server/core/storage';
import { LocalStorageAdapter, type WebStorageLike } from '../../server/core/storage-local';
import { Emitter, type Transport, TransportError, type TransportStatus } from './transport';

export interface InProcessBackendOptions {
  /** `localStorage` (default in browsers), `memory` (default in Node) or a custom adapter. */
  storage?: 'localStorage' | 'memory' | StorageAdapter;
  /** Web Storage to use for `localStorage` (defaults to globalThis.localStorage). */
  webStorage?: WebStorageLike;
  storageKeyPrefix?: string;
  /**
   * Local nickname/password accounts (auth.signUp / auth.signIn; PBKDF2 via Web Crypto). Default: on wherever Web Crypto
   * is available (the offline game signs up like the online one); false turns them off (guest accounts only).
   */
  enableCredentials?: boolean;
  passwordHasher?: PasswordHasher | null;
  clock?: Clock;
  /**
   * Overrides of the in-process defaults (defaultConfig('in-process')): trust level L2 like the self-hosted server
   * (the game changes its state only through player.command), plus the offline conveniences allowLegacyImport (a
   * local or legacy save may replace a fresh profile) and devProfiles (`?dev=1`). Profiles an older client saved in
   * this storage at L0 load as they are: profile.load enters the stored state (session.enter) like any other.
   */
  config?: DeepPartial<BackendConfig>;
  coreOptions?: Partial<BackendCoreOptions>;
  /** Run the background sweeper (auction resolution, deadlines). Default true. */
  sweeper?: boolean;
}

function globalWebStorage(): WebStorageLike | undefined {
  try {
    const ls = (globalThis as { localStorage?: WebStorageLike }).localStorage;
    return ls ?? undefined;
  } catch {
    return undefined;
  }
}

/** Creates a backend core suitable for running inside the game (browser or Node). */
export async function createInProcessBackend(opts: InProcessBackendOptions = {}): Promise<BackendCore> {
  let storage: StorageAdapter;
  if (opts.storage && typeof opts.storage === 'object') storage = opts.storage;
  else {
    const ws = opts.webStorage ?? globalWebStorage();
    const kind = opts.storage ?? (ws ? 'localStorage' : 'memory');
    if (kind === 'localStorage') {
      if (!ws) throw new Error('localStorage is not available in this environment');
      storage = new LocalStorageAdapter(ws, opts.storageKeyPrefix);
    } else storage = new MemoryStorage();
  }
  const hasher =
    opts.passwordHasher !== undefined ? opts.passwordHasher : opts.enableCredentials !== false && WebCryptoPbkdf2Hasher.isAvailable() ? new WebCryptoPbkdf2Hasher() : null;
  const core = await BackendCore.create({ ...(opts.coreOptions ?? {}), storage, mode: 'in-process', clock: opts.clock, passwordHasher: hasher, config: opts.config });
  if (opts.sweeper ?? true) core.startSweeper();
  return core;
}

export class InProcessTransport implements Transport {
  readonly kind = 'in-process' as const;
  readonly connectionBound = true;
  private ctx: ConnectionContext | null = null;
  private readonly events = new Emitter<EventEnvelope>();
  private readonly status = new Emitter<TransportStatus>();
  private closed = false;

  /**
   * @param core backend core (see createInProcessBackend)
   * @param ownsCore close the core when the transport is closed
   */
  constructor(
    readonly core: BackendCore,
    private readonly ownsCore = false,
  ) {}

  async connect(): Promise<void> {
    if (this.closed) throw new TransportError('Transport is closed', false);
    if (this.ctx) return;
    this.ctx = this.core.openConnection({
      transport: 'in-process',
      push: (ev) => {
        const copy = cloneJson(ev);
        queueMicrotask(() => this.events.emit(copy));
      },
    });
    this.status.emit('open');
  }

  async send(request: RequestEnvelope): Promise<ResponseEnvelope> {
    await this.connect();
    const response = await this.core.handle(this.ctx!, cloneJson(request));
    return cloneJson(response);
  }

  onEvent(listener: (event: EventEnvelope) => void): () => void {
    return this.events.on(listener);
  }

  onStatus(listener: (status: TransportStatus) => void): () => void {
    return this.status.on(listener);
  }

  setAuthToken(): void {}

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    if (this.ctx) this.core.closeConnection(this.ctx);
    this.ctx = null;
    this.status.emit('closed');
    if (this.ownsCore) await this.core.close();
  }
}
